import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, Plus, Minus, Star, Clock, Repeat, Share2 } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';
import { getRestaurantStatus } from '@/utils/restaurantStatus';
import { ItemCustomizationDialog } from '@/components/ItemCustomizationDialog';
import { ProductDetailsModal } from '@/components/ProductDetailsModal';
import { SwapRequestDialog } from '@/components/SwapRequestDialog';
import { formatPrice } from '@/lib/utils';
import { getAnySalePrice, getDisplayPrice } from '@/utils/salePricing';

interface Vendor {
  id: string;
  name: string;
  description: string;
  image_url: string;
  rating: number;
  address: string;
  is_open: boolean;
  opening_hours: any;
  avg_prep_time_min: number;
  avg_prep_time_max: number;
  business_type: string;
  categories: string[];
}

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  category_id: string;
  is_customizable: boolean;
  is_available: boolean;
}

interface ExtendedMenuItem extends MenuItem {
  restaurant_id: string;
  restaurant_name: string;
}

interface Category {
  id: string;
  name: string;
}

const MarketplaceVendorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { items, addItem, updateQuantity, getTotalItems, getRestaurantId, orderType, setOrderType, setCartType, deliveryType, total } = useCart();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [averageRating, setAverageRating] = useState<number>(0);
  const [ratingCount, setRatingCount] = useState<number>(0);
  const [customizationDialog, setCustomizationDialog] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState<ExtendedMenuItem | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});
  const [productDetailsModal, setProductDetailsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [selectedProductCategory, setSelectedProductCategory] = useState<string>('');
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);

  useEffect(() => {
    setCartType('marketplace'); // Set cart to marketplace mode
  }, [setCartType]);

  useEffect(() => {
    if (id) {
      fetchVendorData();
      fetchVendorRating();
      
      const channel = supabase
        .channel('vendor-status-changes')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'restaurants',
            filter: `id=eq.${id}`
          },
          (payload) => {
            setVendor(payload.new as Vendor);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [id]);

  const fetchVendorData = async () => {
    try {
      const { data: vendorData, error: vendorError } = await supabase
        .from('restaurants')
        .select('*')
        .eq('id', id)
        .single();

      if (vendorError) throw vendorError;
      setVendor(vendorData);

      const { data: categoriesData, error: categoriesError } = await supabase
        .from('menu_categories')
        .select('*')
        .eq('restaurant_id', id)
        .order('sort_order');

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);

      const { data: menuItemsData, error: menuItemsError } = await supabase
        .from('menu_items')
        .select('*, is_customizable, is_available')
        .eq('restaurant_id', id);

      if (menuItemsError) throw menuItemsError;
      setMenuItems(menuItemsData || []);
      
      const itemId = searchParams.get('item');
      if (itemId && menuItemsData) {
        const requestedItem = menuItemsData.find(item => item.id === itemId);
        if (requestedItem && requestedItem.is_customizable && vendorData) {
          setSelectedMenuItem({
            ...requestedItem,
            restaurant_id: vendorData.id,
            restaurant_name: vendorData.name
          });
          setCustomizationDialog(true);
        }
      }
    } catch (error) {
      console.error('Error fetching vendor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorRating = async () => {
    try {
      const { data: ratingsData, error } = await supabase
        .from('ratings')
        .select('restaurant_rating')
        .eq('restaurant_id', id)
        .not('restaurant_rating', 'is', null);

      if (error) {
        console.error('Error fetching ratings:', error);
        return;
      }

      if (ratingsData && ratingsData.length > 0) {
        const sum = ratingsData.reduce((total, rating) => total + rating.restaurant_rating, 0);
        const avg = sum / ratingsData.length;
        setAverageRating(avg);
        setRatingCount(ratingsData.length);
      }
    } catch (error) {
      console.error('Error fetching vendor rating:', error);
    }
  };

  const handleAddToCart = (item: MenuItem, categoryName?: string) => {
    if (!vendor) return;

    if (!item.is_available) {
      toast.error('This item is currently out of stock');
      return;
    }

    const statusInfo = getRestaurantStatus(vendor);
    if (!statusInfo.canOrder) {
      toast.error('Vendor is currently closed');
      return;
    }

    const currentRestaurantId = getRestaurantId();
    if (currentRestaurantId && currentRestaurantId !== vendor.id) {
      if (confirm('Adding this item will clear your current cart. Continue?')) {
        if (item.is_customizable) {
          setSelectedMenuItem({
            ...item,
            restaurant_id: vendor.id,
            restaurant_name: vendor.name
          });
          setCustomizationDialog(true);
        } else {
          addItemToCart(item, undefined, categoryName);
        }
      }
    } else {
      if (item.is_customizable) {
        setSelectedMenuItem({
          ...item,
          restaurant_id: vendor.id,
          restaurant_name: vendor.name
        });
        setCustomizationDialog(true);
      } else {
        addItemToCart(item, undefined, categoryName);
      }
    }
  };

  const addItemToCart = (item: any, customizations?: any[], categoryName?: string) => {
    if (!vendor) return;

    // Get sale price if applicable
    const finalPrice = getDisplayPrice(vendor.name, item.name, categoryName || '', Number(item.price));

    addItem({
      menu_item_id: item.id,
      name: item.name,
      price: finalPrice,
      quantity: 1,
      restaurant_id: vendor.id,
      restaurant_name: vendor.name,
      image_url: item.image_url,
      customizations: customizations || [],
      referral_post_id: null,
      cart_type: 'marketplace',
    });
    
  };

  const getItemQuantity = (menuItemId: string) => {
    const cartItem = items.find(item => item.menu_item_id === menuItemId);
    return cartItem ? cartItem.quantity : 0;
  };

  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Group menu items by category
  const groupedItems = categories.reduce((acc, category) => {
    const categoryItems = menuItems.filter(item => item.category_id === category.id);
    if (categoryItems.length > 0) {
      acc[category.id] = {
        category,
        items: categoryItems
      };
    }
    return acc;
  }, {} as { [key: string]: { category: Category; items: MenuItem[] } });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center bg-card rounded-2xl p-8 shadow-lg">
          <p className="text-muted-foreground mb-6 text-lg">Vendor not found</p>
          <Button onClick={() => navigate('/home')} className="bg-destructive hover:bg-destructive/90">
            Go Back Home
          </Button>
        </div>
      </div>
    );
  }

  const statusInfo = getRestaurantStatus(vendor);
  const displayRating = averageRating > 0 ? averageRating : vendor.rating;
  
  const prepTimeMin = vendor.avg_prep_time_min || 20;
  const prepTimeMax = vendor.avg_prep_time_max || 30;

  const totalItems = getTotalItems();

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="flex-shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-foreground flex-1 truncate">
            {vendor.name}
          </h1>
          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0"
            onClick={async () => {
              const shareUrl = `${window.location.origin}/marketplace/vendor/${id}`;
              const shareData = {
                title: vendor?.name || 'Store',
                text: `Check out ${vendor?.name} on GAMBGO! 🛒`,
                url: shareUrl,
              };
              if (navigator.share) {
                try { await navigator.share(shareData); } catch {}
              } else {
                await navigator.clipboard.writeText(`${shareData.text}\n${shareUrl}`);
                toast.success('Link copied to clipboard!');
              }
            }}
          >
            <Share2 className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="px-4 py-4">
        {/* Vendor Info */}
        <div className="mb-6 space-y-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              <span className="text-lg font-semibold">{displayRating.toFixed(1)}</span>
              <span className="text-muted-foreground">({ratingCount})</span>
            </div>
            
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span className="text-sm">{statusInfo.statusText}</span>
            </div>
          </div>

          <div className="flex items-center gap-8 text-sm">
            <div>
              <div className="text-muted-foreground">Shopping time</div>
              <div className="font-semibold text-foreground">{prepTimeMin} - {prepTimeMax} min</div>
            </div>
            <div>
              <div className="text-muted-foreground">Available Delivery Type</div>
              <div className="font-semibold text-foreground">Instant</div>
            </div>
          </div>
        </div>

        {/* Swap Button for Technology Vendors */}
        {vendor.categories?.includes('technology') && (
          <div className="mb-6">
            <Button
              onClick={() => setSwapDialogOpen(true)}
              variant="outline"
              className="w-full h-12 text-base font-semibold border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
            >
              <Repeat className="mr-2 h-5 w-5" />
              Trade-In Your Device
            </Button>
          </div>
        )}

        {/* Categories and Items */}
        <div className="space-y-8">
          {Object.entries(groupedItems).map(([categoryId, { category, items: categoryItems }]) => {
            const isExpanded = expandedCategories[categoryId];
            const displayItems = isExpanded ? categoryItems : categoryItems.slice(0, 6);
            const hasMore = categoryItems.length > 6;

            return (
              <div key={categoryId}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-foreground">{category.name}</h2>
                  {hasMore && (
                    <Button
                      variant="ghost"
                      onClick={() => toggleCategoryExpansion(categoryId)}
                      className="text-primary hover:text-primary/80"
                    >
                      {isExpanded ? 'Show less' : `View all (${categoryItems.length})`}
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {displayItems.map((item) => {
                    const quantity = getItemQuantity(item.id);
                    const isOutOfStock = !item.is_available;

                    return (
                      <div key={item.id} className="bg-card rounded-xl overflow-hidden border border-border">
                        <div 
                          className="aspect-square relative bg-muted cursor-pointer"
                          onClick={() => {
                            setSelectedProduct(item);
                            setSelectedProductCategory(category.name);
                            setProductDetailsModal(true);
                          }}
                        >
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-2xl">
                              📦
                            </div>
                          )}
                          {isOutOfStock && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                              <div className="bg-background/90 px-3 py-1.5 rounded-full">
                                <span className="text-xs font-semibold text-destructive">Out of stock</span>
                              </div>
                            </div>
                          )}
                          
                          {/* Add to cart button - positioned at image corner */}
                          {!isOutOfStock && (
                            quantity > 0 ? (
                              <div 
                                className="absolute bottom-1.5 right-1.5 flex items-center gap-0.5 bg-background rounded-full p-0.5 shadow-md"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-5 w-5 rounded-full hover:bg-destructive hover:text-white"
                                  onClick={() => updateQuantity(items.find(i => i.menu_item_id === item.id)?.id || '', quantity - 1)}
                                >
                                  <Minus className="h-3 w-3" />
                                </Button>
                                <span className="font-semibold text-foreground px-1 text-xs">{quantity}</span>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-5 w-5 rounded-full hover:bg-destructive hover:text-white"
                                  onClick={() => updateQuantity(items.find(i => i.menu_item_id === item.id)?.id || '', quantity + 1)}
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddToCart(item, category.name);
                                }}
                                className="absolute bottom-1.5 right-1.5 h-6 w-6 rounded-full bg-destructive hover:bg-destructive/90 text-white shadow-md"
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </Button>
                            )
                          )}
                        </div>
                        
                        <div className="p-3 space-y-1">
                          {(() => {
                            const categoryName = category.name || '';
                            const sale = getAnySalePrice(vendor.name, item.name, categoryName, item.price);
                            if (sale) {
                              return (
                                <div className="flex flex-col">
                                  <div className="text-xs text-muted-foreground line-through decoration-destructive">
                                    D{formatPrice(sale.originalPrice)}
                                  </div>
                                  <div className="text-lg font-bold text-foreground">
                                    D{formatPrice(sale.salePrice)}
                                  </div>
                                </div>
                              );
                            }
                            return (
                              <div className="text-lg font-bold text-foreground">
                                D{formatPrice(item.price)}
                              </div>
                            );
                          })()}
                          <div className="text-sm text-foreground line-clamp-2 min-h-[2.5rem]">
                            {item.name}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {customizationDialog && selectedMenuItem && (
        <ItemCustomizationDialog
          isOpen={customizationDialog}
          onClose={() => setCustomizationDialog(false)}
          menuItem={selectedMenuItem}
          onAddToCart={(item, customizations) => {
            addItemToCart(selectedMenuItem, customizations);
            setCustomizationDialog(false);
          }}
        />
      )}

      {productDetailsModal && selectedProduct && vendor && (
        <ProductDetailsModal
          isOpen={productDetailsModal}
          onClose={() => setProductDetailsModal(false)}
          item={selectedProduct}
          currentQuantity={getItemQuantity(selectedProduct.id)}
          onAddToCart={() => handleAddToCart(selectedProduct, selectedProductCategory)}
          onUpdateQuantity={(newQuantity) => {
            const cartItem = items.find(i => i.menu_item_id === selectedProduct.id);
            if (cartItem) {
              updateQuantity(cartItem.id, newQuantity);
            }
          }}
          restaurantId={vendor.id}
          deliveryType={deliveryType}
          vendorName={vendor.name}
          categoryName={selectedProductCategory}
        />
      )}

      {/* Swap Request Dialog */}
      <SwapRequestDialog
        open={swapDialogOpen}
        onOpenChange={setSwapDialogOpen}
        restaurantId={id!}
        vendorId={vendor.id}
        menuItems={menuItems.map(item => ({ id: item.id, name: item.name, price: item.price }))}
      />

      {/* View Cart Button */}
      {totalItems > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4 z-40">
          <Button 
            className="w-full bg-destructive hover:bg-destructive/90 text-white"
            onClick={() => navigate('/marketplace/cart')}
          >
            View Cart ({totalItems} {totalItems === 1 ? 'item' : 'items'}) - D{total.toFixed(2)}
          </Button>
        </div>
      )}
    </div>
  );
};

export default MarketplaceVendorPage;
